import { ReactElement, useEffect, useMemo, useState } from 'react';
import { SafeArea } from 'capacitor-plugin-safe-area';
import './App.css';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Data from './pages/data.page';
import AlertsPage from './pages/alerts.page';
import ReportsPage from './pages/reports.page';
import InsightPage from './pages/insights.page';
import TelosAgentPage from './pages/ai-agent.page';
import SettingsPage from './pages/settings.page';
import Layout from './components/Layout';
import { ThemeContextProvider } from './context/theme.context';
import Dashboard from './pages/dashboard.page';
import Support from './pages/support.page';
import HelpCenter from './pages/help-center.page';
import { UserContextProvider } from './context/user.context';
import DeviceSetupPage from './pages/devices.page';
import UserManagementPage from './pages/user-management.page';
import AlertRulesPage from './pages/alert-rules.page';
import { AuthenticationProvider } from './context/auth.context';
import ProtectedRoute from './components/ProtectedRoute';
import Integration from './pages/integration.page';
import ParameterSetupPage from './pages/parameter.page';
import Profile from './pages/profile.page';
import Organisation from './pages/organisation.page';
import AccountActivationDeviceSetup from './components/AccountActivation/steps/device-setup';
import AccountActivationGroupSetup from './components/AccountActivation/steps/group-setup';
import AccountActivationInviteUser from './components/AccountActivation/steps/invite-users';
import AuthenticationAndActivation from './pages/auth.page';
import NotificationSettings from './pages/notification.page';
import Pricing from './pages/pricing.page';
import PaypalSuccessPage from './pages/paypal-success.page';
import PaypalCancelPage from './pages/paypal-cancel.page';
import AdminPaymentsPage from './pages/admin-payments.page';
import AdminAiSessionsPage from './pages/admin-ai-sessions.page';
import AdminBlePingsPage from './pages/admin-ble-pings.page';
import OpcBinsPage from './pages/opc-bins.page';
import ApsChannelsPage from './pages/aps-channels.page';
import PaymentHistoryPage from './pages/payment-history.page';
import NoMatch from './pages/nomatch';
import { DataProvider } from './context/data.context';
import { PublicDataProvider } from './context/public-data.context';
import ConnectDevicesPage from './pages/connect-devices.page';
import Calibration from './pages/calibration.page';
import CalibrationData from './pages/calibration-data.page';
import RequestDeletionPage from './pages/request-deletion.page';
import OnboardingLayout from './components/OnboardingLayout';
import OnboardingAddDevice from './components/Onboarding/AddDevice';
import OnboardingConnectInternet from './components/Onboarding/ConnectInternet';
import OnboardingConfigureDevice from './components/Onboarding/ConfigureDevice';
import OnboardingAddGroups from './components/Onboarding/AddGroups';
import OnboardingInviteUsers from './components/Onboarding/InviteUsers';
import GeneratePublicLinkPage from './pages/generate-public-link';
import PublicDashboardPage from './pages/public-dashboard.page';
import { SubdomainProvider, useSubdomain } from './context/subdomain.context';

const userContextValue = {
  full_name: 'Kevin Ashton',
  short_name: 'Kevin',
  profile_picture: `${process.env.PUBLIC_URL}/assets/avatar.png`,
  subscription_type: 'Standard'
};

/**
 * Component that renders routes based on subdomain.
 * If not dashboard subdomain, only shows public dashboard.
 * @returns A React element with appropriate routes.
 */
function AppRoutes(): ReactElement {
  const { isDashboardSubdomain } = useSubdomain();

  // If NOT dashboard subdomain, only show public dashboard with PublicDataProvider
  // eslint-disable-next-line no-self-compare, no-constant-condition
  if (!isDashboardSubdomain) {
    // TODO: Change to !isDashboardSubdomain
    return (
      <Routes>
        <Route element={<PublicDataProvider />}>
          <Route path="*" element={<PublicDashboardPage />} />
        </Route>
      </Routes>
    );
  }

  // If dashboard subdomain, show all normal routes with full authentication
  return (
    <Routes>
      {/* Public Routes - No Authentication Required */}
      <Route element={<PublicDataProvider />}>
        <Route path="/public" element={<PublicDashboardPage />} />
      </Route>

      {/* Protected Routes - Authentication Required */}
      <Route path="/" element={<ProtectedRoute />}>
        <Route index path="login" element={<Navigate to="/auth/login" />} />
        <Route index path="auth/:type" element={<AuthenticationAndActivation />} />
        <Route element={<DataProvider />}>
          <Route
            path="account-activation/device-setup"
            element={<AccountActivationDeviceSetup />}
          />
          <Route path="account-activation/group-setup" element={<AccountActivationGroupSetup />} />
          <Route path="account-activation/invite-users" element={<AccountActivationInviteUser />} />
          <Route path="onboarding" element={<OnboardingLayout />}>
            <Route path="add-devices" element={<OnboardingAddDevice />} />
            <Route path="connect-internet" element={<OnboardingConnectInternet />} />
            <Route path="configure-devices" element={<OnboardingConfigureDevice />} />
            <Route path="add-groups" element={<OnboardingAddGroups />} />
            <Route path="invite-users" element={<OnboardingInviteUsers />} />
          </Route>
          <Route element={<Layout />}>
            <Route index element={<Dashboard />} />
            <Route path="activity" element={<AlertsPage />} />
            <Route path="alert-rules" element={<AlertRulesPage />} />
            <Route path="data" element={<Data />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="reports/:id" element={<ReportsPage />} />
            <Route path="insights" element={<InsightPage />} />
            <Route path="ai-agent" element={<TelosAgentPage />} />
            <Route path="/settings/integrations" element={<Integration />} />
            <Route path="/settings/device-setup" element={<DeviceSetupPage />} />
            <Route path="/settings/user-management" element={<UserManagementPage />} />
            <Route path="support" element={<Support />} />
            <Route path="help-center" element={<HelpCenter />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="/settings/notification" element={<NotificationSettings />} />
            <Route path="/settings/parameter" element={<ParameterSetupPage />} />
            <Route path="/settings/profile" element={<Profile />} />
            <Route path="/settings/organization" element={<Organisation />} />
            <Route path="/settings/generate-public-link" element={<GeneratePublicLinkPage />} />
            <Route path="/settings/calibration" element={<Calibration />} />
            <Route path="/settings/calibration-data" element={<CalibrationData />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="pricing/paypal-success" element={<PaypalSuccessPage />} />
            <Route path="pricing/paypal-cancel" element={<PaypalCancelPage />} />
            <Route path="admin/payments" element={<AdminPaymentsPage />} />
            <Route path="admin/ai-sessions" element={<AdminAiSessionsPage />} />
            <Route path="admin/ble-pings" element={<AdminBlePingsPage />} />
            <Route path="opc-bins" element={<OpcBinsPage />} />
            <Route path="aps-channels" element={<ApsChannelsPage />} />
            <Route path="billing/history" element={<PaymentHistoryPage />} />
            <Route path="connect-devices" element={<ConnectDevicesPage />} />
            <Route path="deletion-request" element={<RequestDeletionPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<NoMatch />} />
    </Routes>
  );
}

/**
 * Main application component that sets up routing for the application.
 * @returns  A React element representing the App component.
 */
export default function App(): ReactElement {
  const [isDarkTheme, setIsDarkTheme] = useState(true);
  const [loading, setLoading] = useState(true);
  /**
   * ToggleTheme function is used to toggle the theme of the application.
   */
  const toggleTheme = (): void => {
    setIsDarkTheme(!isDarkTheme);
  };

  // Use useMemo to memoize the context value
  const themeContextValue = useMemo(
    () => ({
      isDarkTheme,
      toggleTheme
    }),
    [isDarkTheme]
  );

  // useEffect(() => {
  //   if (isDarkTheme) {
  //     document.documentElement.classList.add('dark');
  //   } else {
  //     document.documentElement.classList.remove('dark');
  //   }
  // }, [isDarkTheme]);

  useEffect(() => {
    const isCleared = localStorage.getItem('is_cleared');
    if (!isCleared) {
      localStorage.clear();
      localStorage.setItem('is_cleared', 'yes');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line jsdoc/require-jsdoc
    const initializeSafeArea = async (): Promise<void> => {
      const safeAreaData = await SafeArea.getSafeAreaInsets();
      const { insets } = safeAreaData;
      // eslint-disable-next-line no-restricted-syntax
      for (const [key, value] of Object.entries(insets)) {
        document.documentElement.style.setProperty(`--safe-area-inset-${key}`, `${value}px`);
      }
    };

    initializeSafeArea();
  }, []);

  if (loading) {
    return <div />;
  }

  return (
    <div className="pb-safe pt-safe flex flex-1 flex-col">
      <ThemeContextProvider value={themeContextValue}>
        <AuthenticationProvider>
          <UserContextProvider value={userContextValue}>
            <SubdomainProvider>
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
              <ToastContainer
                position="bottom-left"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                theme="dark"
              />
            </SubdomainProvider>
          </UserContextProvider>
        </AuthenticationProvider>
      </ThemeContextProvider>
    </div>
  );
}
